import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthenticationService } from "../../services/authentication.service";
import { Sidenav, Nav, Image } from "rsuite";
import { roles } from "../../utils/constants";
import DarkModeToggle from "../dark-mode-toggle";
import { BESTSTART_LOGO_WHITE_SMALL, SMALL_ICON } from "../../utils/constants/images";
import { version } from "../../../package.json";
import { IoHome } from "react-icons/io5";
import { FaFileCsv, FaInbox, FaDollarSign } from "react-icons/fa";
import { IoMdLogOut } from "react-icons/io";
import { HiOfficeBuilding } from "react-icons/hi";
import { useTheme } from "../../contexts/ThemeContext";
import { AppSwitcher } from "@best-start/beststart.react.app-switcher";
import { Hamburger } from "..";
import "./navigation.css";

const APP_SWITCHER_CONFIG_URL = "https://cdn.best-start.org/app-switcher/app-switcher.config.json";

function Navigation({ setMargin }) {
  const MIN_MARGIN = 50;
  const MAX_MARGIN = 200;
  const navRef = useRef(null);
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [activeKey, setActiveKey] = useState("1");
  const navigate = useNavigate();
  const AuthenticationService = useAuthenticationService();
  const { darkMode, setDarkMode } = useTheme();
  const handleSignOut = async () => {
    await AuthenticationService.logout();
  };
  const paths = useMemo(
    () => [
      { icon: <IoHome className="rs-icon" />, path: "/", key: "1", name: "Home", visible: true },
      { icon: <FaInbox className="rs-icon" />, path: "/inbox", key: "2", name: "Inbox", visible: true },
      { icon: <HiOfficeBuilding className="rs-icon" />, path: "/centres", key: "3", name: "Centres", visible: true },
      {
        icon: <FaFileCsv className="rs-icon" />,
        path: "/export",
        key: "4",
        name: "Export",
        visible: AuthenticationService.isInAnyRole(roles.ADMINS),
      },
      {
        icon: <FaDollarSign className="rs-icon" />,
        path: "/winzrates",
        key: "5",
        name: "Rates",
        visible: AuthenticationService.isInAnyRole(roles.ADMINS),
      },
      {
        icon: <IoMdLogOut className="rs-icon" />,
        path: "/signout",
        key: "9",
        name: "Log Out",
        testId: "logout-button",
        footer: true,
        visible: true,
      },
    ],
    []
  );
  function matchPath(path, activePath) {
    // Remove query params for comparison
    const cleanPath = path.split("?")[0];
    const cleanActivePath = activePath.split("?")[0];

    // Exact match
    if (cleanPath === cleanActivePath) return true;

    // Check if active path starts with the nav path (for sub-routes)
    // This allows /centres to match /centres/1, /centres/edit, etc.
    if (cleanPath !== "/" && cleanActivePath.startsWith(cleanPath + "/")) {
      return true;
    }

    return false;
  }

  function navigateTopath(path) {
    if (location.pathname === path) {
      setExpanded(false);
      setMargin && setMargin(MIN_MARGIN);
      return;
    }
    path === "/signout" ? handleSignOut() : navigate(path);
    setExpanded(false);
    setMargin && setMargin(MIN_MARGIN);
  }

  const handleClickOutside = (event) => {
    if (navRef.current && !navRef.current.contains(event.target)) {
      setExpanded(false);
      setMargin && setMargin(MIN_MARGIN);
    }
  };

  function onToggle(value) {
    setDarkMode(value);
  }

  function sendClick(event) {
    setExpanded(event.target.checked);
    setMargin && setMargin(event.target.checked ? MAX_MARGIN : MIN_MARGIN);
  }

  useEffect(() => {
    const activeKey = paths.find((item) => matchPath(item.path, location.pathname));
    setActiveKey(activeKey ? activeKey.key : "1");
  }, [location.pathname, paths]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    const isInRole = AuthenticationService.isInAnyRole([roles.ROLE_SUPER_ADMINISTRATOR, roles.ROLE_READ_ONLY]);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleSwipe = (event) => {
      const touch = event.touches[0];
      const swipeThreshold = 100; // Minimum distance for a swipe to be detected

      if (event.type === "touchstart") {
        navRef.current.startX = touch.clientX;
      } else if (event.type === "touchmove") {
        const deltaX = touch.clientX - navRef.current.startX;

        if (deltaX > swipeThreshold) {
          // Swipe from left to right
          setExpanded(true);
          setMargin && setMargin(MAX_MARGIN);
        } else if (deltaX < -swipeThreshold) {
          // Swipe from right to left
          setExpanded(false);
          setMargin && setMargin(MIN_MARGIN);
        }
      }
    };
    setMargin && setMargin(expanded ? MAX_MARGIN : MIN_MARGIN);

    document.addEventListener("touchstart", handleSwipe);
    document.addEventListener("touchmove", handleSwipe);

    return () => {
      document.removeEventListener("touchstart", handleSwipe);
      document.removeEventListener("touchmove", handleSwipe);
    };

  }, []);

  return (
    <div
      style={{ position: "fixed", width: expanded ? MAX_MARGIN : 'auto', display: "flex", justifyContent: "space-between", flexDirection: "column", height: "100vh", zIndex: 9000 }}
      ref={navRef}
    >
      <Sidenav expanded={expanded} className="side-bar" data-testid="navbar" data-expanded={expanded}>
        <Sidenav.Body style={{ paddingTop: 10, zIndex: 9999 }}>
          <div className={`hamburger-wrapper ${expanded ? "expanded" : ""}`}>
            <Hamburger checked={expanded} onClick={sendClick} />
          </div>

          <div className="logo-container">
            {/* done this so it preloads the images */}
            <Image src={BESTSTART_LOGO_WHITE_SMALL} alt="BestStart Logo" width="100%" height={45} style={{ display: expanded ? "" : "none" }} />
            <Image src={SMALL_ICON} alt="BestStart Logo" width="100%" height={45} style={{ display: !expanded ? "" : "none" }} />
          </div>
          <AppSwitcher
            className="app-switcher-container"
            currentAppId="winz-portal"
            authenticationService={AuthenticationService}
            manifestUrl={APP_SWITCHER_CONFIG_URL}
          />
          <Nav activeKey={activeKey} onSelect={setActiveKey}>
            {paths &&
              paths
                .filter((path) => !path.footer)
                .map((item) => {
                  return (
                    <Nav.Item
                      data-testid={`${item.testId || item.key}-web`}
                      key={item.key}
                      onClick={navigateTopath.bind(null, item.path)}
                      eventKey={item.key}
                      icon={item.icon}
                      disabled={!item.visible}
                    >
                      {item.name}
                    </Nav.Item>
                  );
                })}
          </Nav>
        </Sidenav.Body>
        <div className={`user-info ${expanded ? "visible" : ""}`}>
          <div className="version">Version: {version}</div>
        </div>
        <Nav activeKey={activeKey} onSelect={setActiveKey} className="bottom-nav">
          <DarkModeToggle onToggle={onToggle} defaultChecked={darkMode} />
          {paths &&
            paths
              .filter((path) => path.footer)
              .map((item) => {
                return (
                  <Nav.Item
                    data-testid={`${item.testId || item.key}-footer`}
                    key={item.key}
                    onClick={navigateTopath.bind(null, item.path)}
                    eventKey={item.key}
                    icon={item.icon}
                  >
                    {item.name}
                  </Nav.Item>
                );
              })}
        </Nav>
      </Sidenav>
    </div>
  );
}

export default Navigation;
